import '@xyflow/react/dist/style.css';
import {
  Background,
  ConnectionMode,
  Controls,
  ReactFlow,
  useReactFlow,
  XYPosition,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
} from '@xyflow/react';
import { nodeTypes } from './nodes';
import { edgeTypes } from '../../../types/edgeTypes';
import { useDiagramStore } from './hooks/diagram-store.hook';
import { NodeId, NodeModel } from '../model/node.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  WheelEvent as ReactWheelEvent,
  DragEvent as ReactDragEvent,
} from 'react';
import { useSendOp } from './hooks/send-op.hook';
import { getOS } from '../../../utils/getOS';
import { useDragMonitor } from '../../../store/dragMonitorStore';
import { useDnD } from '../../../context/DnDContext';
import NavigatingMiniMap from './diagram.minimap';
import { EdgeId, EdgeModel } from '@diagram/model/edge.model';
import { ChangeModel } from '@diagram/model/change.model';

const DiagramCanvas = () => {
  const { screenToFlowPosition, getViewport, setViewport } = useReactFlow();
  const { setDraggingNode, setHoveredEdge, hoveredEdgeId } = useDragMonitor();
  const nodes = useDiagramStore((s) => s.rfNodes);
  const edges = useDiagramStore((s) => s.rfEdges);
  const onConnect = useDiagramStore((s) => s.edge.onConnect);
  const subscribeToSession = useDiagramStore((s) => s.subscribeToSession);
  const unsubscribeFn = useDiagramStore((s) => s.unsubscribeFn);
  const applyNodesChanges = useDiagramStore((s) => s.applyNodesChanges);
  const applyEdgesChanges = useDiagramStore((s) => s.applyEdgesChanges);
  const createNodeAt = useDiagramStore((s) => s.createNodeAt);
  const startOperation = useDiagramStore((s) => s.startOperation);
  const finalizeOperation = useDiagramStore((s) => s.finalizeOperation);
  const setEdges = useDiagramStore((s) => s.setEdges);
  const setNodes = useDiagramStore((s) => s.setNodes);
  const getUsedHandles = useDiagramStore((s) => s.getUsedHandles);
  const onReconnect = useDiagramStore((s) => s.edge.onReconnect);
  const createSwimlaneGroup = useDiagramStore(
    (s) => s.swimlane.createSwimlaneGroup
  );
  const recordOperation = useDiagramStore((s) => s.recordOperation);
  const dndContext = useDnD();

  const canEdit = true; // Placeholder, replace with actual permission logic

  const [isMultiSelect, setIsMultiSelecting] = useState<
    'drag' | 'select' | null
  >(null);
  const key = getOS() === 'MacOS' ? 'Cmd' : 'Ctrl';
  const data = dndContext.data;
  const zoomSpeed = 0.002; // adjust this multiplier to increase or decrease zoom speed
  const minZoom = 0.7;
  const maxZoom = 4;

  useSendOp();

  useEffect(() => {
    const keyMap: Record<string, 'drag' | 'select' | null> = {
      Shift: 'drag',
      Control: 'select',
      Meta: 'select',
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.type === 'keydown') {
        const mode = keyMap[e.key];
        if (mode) setIsMultiSelecting(mode);
      } else if (e.type === 'keyup') {
        if (keyMap[e.key]) setIsMultiSelecting(null);
      }
    };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKey);

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKey);
    };
  }, []);

  useEffect(() => {
    subscribeToSession();

    return () => {
      if (unsubscribeFn) {
        unsubscribeFn.then((fn) => fn());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToSession]);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const position: XYPosition = screenToFlowPosition({
        x: event.clientX - data.width / 2,
        y: event.clientY - data.height / 2,
      });
      if (!hoveredEdgeId && data.type === 'shape') {
        createNodeAt({ x: position.x, y: position.y, ...data });
      } else if (data.type === 'swimlane') {
        createSwimlaneGroup(data.orientation, position);
      }
    },
    [
      data,
      screenToFlowPosition,
      hoveredEdgeId,
      createNodeAt,
      createSwimlaneGroup,
    ]
  );

  const nodeType = useMemo(() => nodeTypes, []);
  const edgeType = useMemo(() => edgeTypes, []);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrag = useCallback(
    (_: ReactDragEvent<HTMLDivElement>, node: NodeModel) => {
      // some versions of the handler may provide node in the 2nd or 3rd arg,
      // so try to use the node argument first and fall back to any additional args.
      const targetNode = node;
      if (!targetNode) return;
      if (!getUsedHandles(targetNode.id)) {
        setDraggingNode(targetNode.id, targetNode.position);
      }
    },
    [getUsedHandles, setDraggingNode]
  );

  const onNodeDragStart = useCallback(
    (_: React.MouseEvent, node: NodeModel) => {
      startOperation([
        {
          type: 'move',
          id: node?.id as NodeId,
          status: 'start',
          data: {
            position: node.position,
          },
        },
      ]);
    },
    [startOperation]
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: NodeModel) => {
      const { hoveredEdgeId } = useDragMonitor.getState();
      if (hoveredEdgeId && !getUsedHandles(node.id)) {
        // Clear hovered edge state
      } else {
        finalizeOperation([
          {
            type: 'move',
            id: node.id as NodeId,
            status: 'end',
            data: {
              position: node.position,
            },
          },
        ]);
      }
      setHoveredEdge(null);
      setDraggingNode(null, null);
    },
    [finalizeOperation, setHoveredEdge, setDraggingNode, getUsedHandles]
  );
  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      // Get the current viewport: translation and zoom
      const { x: oldX, y: oldY, zoom: oldZoom } = getViewport();

      // Calculate the new zoom level based on wheel delta
      let newZoom = oldZoom - event.deltaY * zoomSpeed;
      newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

      // Use currentTarget which is correctly typed as the HTMLDivElement container
      const bounds = event.currentTarget.getBoundingClientRect();
      const pointerContainer = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };

      // Compute the flow (diagram) coordinates under the pointer using the old viewport values:
      const pointerFlow = {
        x: (pointerContainer.x - oldX) / oldZoom,
        y: (pointerContainer.y - oldY) / oldZoom,
      };

      // Calculate the new translation so that the same flow coordinate stays under the pointer:
      const newX = pointerContainer.x - pointerFlow.x * newZoom;
      const newY = pointerContainer.y - pointerFlow.y * newZoom;

      // Update the viewport with the new translation and zoom
      setViewport({ x: newX, y: newY, zoom: newZoom });
    },
    [getViewport, setViewport]
  );

  const onNodesDelete = useCallback(
    (deleted: NodeModel[]) => {
      let remainingNodes = [...nodes];
      const changes = [] as ChangeModel[];
      setEdges(
        deleted.reduce((acc, node) => {
          const incomers = getIncomers(node, remainingNodes, acc);
          const outgoers = getOutgoers(node, remainingNodes, acc);
          const connectedEdges = getConnectedEdges([node], acc);

          changes.push(
            ...connectedEdges.map(
              (edge) =>
                ({
                  type: 'delete:edge',
                  id: edge.id as EdgeId,
                  status: 'end',
                  data: { ...edge },
                } as ChangeModel)
            )
          );

          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge)
          );

          const createdEdges = incomers.flatMap(({ id: source }) =>
            outgoers.map(
              ({ id: target }) =>
                ({
                  id: `${source}->${target}` as EdgeId,
                  source,
                  target,
                  sourceHandle: connectedEdges.find(
                    (e) => e.source === source && e.target === node.id
                  )?.sourceHandle,
                  targetHandle: connectedEdges.find(
                    (e) => e.source === node.id && e.target === target
                  )?.targetHandle,
                  data: {
                    label: '',
                  },
                  type: 'insertSmooth',
                } as EdgeModel)
            )
          );

          changes.push(
            ...createdEdges.map(
              (edge) =>
                ({
                  type: 'create:edge',
                  id: edge.id as EdgeId,
                  status: 'end',
                  data: { ...edge },
                } as ChangeModel)
            )
          );

          changes.push({
            type: 'delete:node',
            id: node.id as NodeId,
            status: 'end',
            data: { ...node },
          } as ChangeModel);

          remainingNodes = remainingNodes.filter((rn) => rn.id !== node.id);

          return [...remainingEdges, ...createdEdges] as EdgeModel[];
        }, edges)
      );
      setNodes(remainingNodes);
      recordOperation(changes);
    },
    [nodes, edges, setEdges, setNodes, recordOperation]
  );

  return (
    <div className="w-full h-full relative">
      {isMultiSelect && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <div className="text-secondary bg-white text-sm px-3 py-1 rounded shadow pointer-none">
            Multi-select mode{' '}
            {isMultiSelect === 'drag'
              ? '(click and drag to select multiple nodes / edges)'
              : `(hold ${key} and click to select multiple nodes / edges)`}
          </div>
        </div>
      )}

      <ReactFlow
        className="border-2 transition-all w-full h-full"
        style={{
          borderColor: isMultiSelect ? '#27CFC9' : 'transparent', // blue-500
        }}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeType}
        edgeTypes={edgeType}
        onNodesDelete={canEdit ? onNodesDelete : undefined}
        onNodesChange={applyNodesChanges}
        onEdgesChange={applyEdgesChanges}
        onNodeDragStart={canEdit ? onNodeDragStart : undefined}
        // eslint-disable-next-line
        onDrag={
          canEdit
            ? (...args: unknown[]) =>
                onDrag(
                  args[0] as ReactDragEvent<HTMLDivElement>,
                  args[1] as NodeModel
                )
            : undefined
        }
        onDragOver={canEdit ? onDragOver : undefined}
        multiSelectionKeyCode={['Shift', 'Ctrl']}
        onNodeDragStop={canEdit ? onNodeDragStop : undefined}
        onConnect={canEdit ? onConnect : undefined}
        onDrop={canEdit ? onDrop : undefined}
        onReconnect={canEdit ? onReconnect : undefined}
        snapToGrid={true}
        snapGrid={[10, 10]}
        connectionMode={ConnectionMode.Loose}
        zoomOnScroll={false}
        panOnScroll={false}
        fitView={true}
        deleteKeyCode={['Delete', 'Backspace']}
        onWheel={handleWheel}
        // onPaneContextMenu={(e) => {
        //   e.preventDefault();
        //   const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        //   createNodeAt({
        //     type: 'shape',
        //     shape: 'square',
        //     x: pos.x,
        //     y: pos.y,
        //   });
        // }}
      >
        <Background />
        <NavigatingMiniMap />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default DiagramCanvas;

//         onPaneContextMenu={(e) => {
//             e.preventDefault();
//             const pos = rfApi.screenToFlowPosition({ x: e.clientX - 50, y: e.clientY - 50 });
// // quick test template — change to 'swimlane' etc. if you want
//             addNodeAtPos(pos, { type: 'shape', data: { type: 'shape', shape: 'rectangle', label: 'New Node', color: '#000000' } });
//         }}
